import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../components/AppShell';
import { ErrorState, SkeletonRows, SkeletonTiles, StatTile } from '../components/ui';
import { api, ApiRequestError } from '../lib/api';
import { formatINR, formatINRCompact, formatNumber } from '../lib/format';

/**
 * A06 — how the platform is doing.
 *
 * Every chart here is drawn from the daily snapshot table, so the lines are a
 * real history rather than today's number repeated. Where there is not enough
 * history to draw a line, the page says so instead of drawing a flat one —
 * a chart that looks like data but is not is worse than an empty panel.
 */

interface AnalyticsResponse {
  rangeDays: number;
  totals: { schools: number; churned: number; mrrPaise: number; students: number; parents: number };
  daily: {
    date: string;
    students: number;
    parents: number;
    noticesSent: number;
    attendanceRateBps: number;
    feeCollectionBps: number;
    noticeReadRateBps: number;
  }[];
  byPlan: { key: string; name: string; schools: number; mrrPaise: number }[];
  byStatus: { status: string; count: number }[];
  signupsByMonth: { month: string; count: number }[];
  billingByMonth: { month: string; invoicedPaise: number; paidPaise: number }[];
  moduleAdoption: { key: string; name: string; category: string; schools: number; shareBps: number }[];
}

const RANGES = [30, 90, 180, 365];

export function AnalyticsPage() {
  const [days, setDays] = useState(90);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => api.get<AnalyticsResponse>(`/api/analytics?days=${days}`),
  });

  return (
    <>
      <PageHeader
        title="Analytics"
        meta={data ? `${formatNumber(data.totals.schools)} schools · ${formatINRCompact(data.totals.mrrPaise)} MRR` : 'Loading…'}
        actions={
          <div className="segmented" style={{ flex: 'none' }}>
            {RANGES.map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={days === value}
                onClick={() => setDays(value)}
                style={{ padding: '10px 14px', flex: 'none' }}
              >
                {value === 365 ? '1 year' : `${value} days`}
              </button>
            ))}
          </div>
        }
      />

      <div className="page">
        {error ? (
          <ErrorState
            message={error instanceof ApiRequestError ? error.message : 'Could not load analytics'}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <>
            <SkeletonTiles />
            <SkeletonRows />
          </>
        ) : (
          <>
            <div className="grid-4">
              <StatTile
                label="Live schools"
                value={formatNumber(data.totals.schools)}
                caption={data.totals.churned > 0 ? `${data.totals.churned} closed` : 'none closed'}
                dark
              />
              <StatTile label="MRR" value={formatINR(data.totals.mrrPaise)} caption="across every active plan" />
              <StatTile
                label="Students"
                value={formatNumber(data.totals.students)}
                caption="on the platform"
              />
              <StatTile
                label="Parents reached"
                value={formatNumber(data.totals.parents)}
                caption="with an active app account"
              />
            </div>

            <Panel
              title="Students on the platform"
              caption={`Daily, over the last ${data.rangeDays} days`}
              empty={data.daily.length < 2 ? 'Not enough history yet — this needs a few days of snapshots.' : null}
            >
              <Sparkline points={data.daily.map((d) => d.students)} labels={data.daily.map((d) => d.date)} />
            </Panel>

            <div className="grid-2">
              <Panel title="Revenue by plan" caption="Monthly recurring, by the plan each school is on" empty={data.byPlan.length === 0 ? 'No schools on a plan yet.' : null}>
                <Bars
                  rows={data.byPlan.map((p) => ({
                    label: p.name,
                    value: p.mrrPaise,
                    caption: `${p.schools} school${p.schools === 1 ? '' : 's'} · ${formatINRCompact(p.mrrPaise)}`,
                  }))}
                />
              </Panel>

              <Panel title="Schools by state" caption="Everything on the books, closed accounts included">
                <Bars
                  rows={data.byStatus.map((s) => ({
                    label: s.status.charAt(0) + s.status.slice(1).toLowerCase().replace(/_/g, ' '),
                    value: s.count,
                    caption: formatNumber(s.count),
                  }))}
                />
              </Panel>
            </div>

            <Panel
              title="Feature adoption"
              caption="How many live schools have each module switched on — what is actually being used"
              empty={data.moduleAdoption.length === 0 ? 'No modules enabled anywhere yet.' : null}
            >
              <Bars
                rows={data.moduleAdoption.map((m) => ({
                  label: m.name,
                  value: m.schools,
                  caption: `${m.schools} of ${data.totals.schools} · ${(m.shareBps / 100).toFixed(0)}%`,
                }))}
              />
            </Panel>

            <Panel
              title="Invoiced and collected"
              caption="By the month each invoice was issued"
              empty={data.billingByMonth.length === 0 ? 'No invoices issued in this window.' : null}
            >
              <div className="card card--flush">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th style={{ textAlign: 'right' }}>Invoiced</th>
                      <th style={{ textAlign: 'right' }}>Collected</th>
                      <th style={{ width: 120, textAlign: 'right' }}>Collected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.billingByMonth.map((row) => {
                      const rate = row.invoicedPaise === 0 ? 0 : Math.round((row.paidPaise / row.invoicedPaise) * 100);
                      return (
                        <tr key={row.month}>
                          <td className="mono">{row.month}</td>
                          <td className="num">{formatINR(row.invoicedPaise)}</td>
                          <td className="num">{formatINR(row.paidPaise)}</td>
                          <td className="num" style={{ color: rate >= 90 ? 'var(--leaf)' : rate >= 60 ? 'var(--ink)' : 'var(--coral)' }}>
                            {rate}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}
      </div>
    </>
  );
}

function Panel({
  title,
  caption,
  empty,
  children,
}: {
  title: string;
  caption: string;
  empty?: string | null;
  children: React.ReactNode;
}) {
  return (
    <div className="card">
      <div className="card__title">{title}</div>
      <div className="caption" style={{ marginTop: 2, marginBottom: 14 }}>
        {caption}
      </div>
      {empty ? <div className="caption">{empty}</div> : children}
    </div>
  );
}

/** Horizontal bars, scaled to the largest row so the shape is readable. */
function Bars({ rows }: { rows: { label: string; value: number; caption: string }[] }) {
  const peak = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 150, fontSize: 13, fontWeight: 600, flex: 'none' }}>{row.label}</div>
          <div style={{ flex: 1, height: 10, background: 'var(--ink-tint)', borderRadius: 999, minWidth: 40 }}>
            <div
              style={{
                width: `${Math.max(2, (row.value / peak) * 100)}%`,
                height: '100%',
                background: 'var(--marigold)',
                borderRadius: 999,
              }}
            />
          </div>
          <div className="caption" style={{ width: 150, textAlign: 'right', flex: 'none' }}>
            {row.caption}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A plain SVG line. No chart library for one line — the whole thing is a
 * polyline and two labels, and a dependency would cost more than it saves.
 */
function Sparkline({ points, labels }: { points: number[]; labels: string[] }) {
  if (points.length < 2) return null;

  const width = 900;
  const height = 160;
  const pad = 8;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = max - min || 1;

  const path = points
    .map((value, index) => {
      const x = pad + (index / (points.length - 1)) * (width - pad * 2);
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 160, display: 'block' }} role="img" aria-label="Students over time">
        <polyline points={path} fill="none" stroke="var(--marigold)" strokeWidth={2.5} strokeLinejoin="round" />
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span className="caption">
          {labels[0]} · {formatNumber(points[0]!)}
        </span>
        <span className="caption">
          {labels.at(-1)} · {formatNumber(points.at(-1)!)}
        </span>
      </div>
    </div>
  );
}
